import { TMkStep } from '@typescript'
import { EMkBookAction, MkBookAction, IMkBookState, IMkAnyBookField, EMkBookStepName, TMkBookStep } from '../types'

const selectFieldSet = (steps: TMkStep[]) => {
  const vipUpgradesStep = steps.find(({ name }) => name === EMkBookStepName.VIP_UPGRADES)
  return vipUpgradesStep ? vipUpgradesStep.fieldSet : {}
}

const generateSuiteTypeByFieldName = (
  fieldSet: {
    [key: string]: IMkAnyBookField
  },
  fieldName: string,
  query: string,
) => {
  if (!fieldSet) return undefined
  if (fieldName === 'vipUpgradeNow') {
    return Object.entries(fieldSet).find(([, { name, value }]) => name === fieldName && value === query)?.[1].label
  }
  if (fieldName === 'vipUpgradeFuture') {
    return Object.entries(fieldSet).find(([, { name, value }]) => name === fieldName && value === query)?.[1].label
  }
  return undefined
}

// Reducer
const bookReducer = (state: IMkBookState, action: MkBookAction): IMkBookState => {
  switch (action.type) {
    case EMkBookAction.SET_BOOK_DRAFT:
      return {
        ...state,
        bookDraft: action.payload,
      }

    case EMkBookAction.UPDATE_DATES_STEP_CONTENT: {
      if (!state.bookDraft?.bookingSteps) return state
      const dates = action.payload
      const updatedSteps = state.bookDraft.bookingSteps.map((step) => {
        if (step.name !== EMkBookStepName.MODIFY_TRIP_DATES) return step
        const content = step.content ?? {}
        const currentDates = (content as any).dates ?? {}
        return {
          ...step,
          content: {
            ...content,
            dates: {
              ...currentDates,
              ...(dates.earliestCheckIn != null && { earliestCheckIn: dates.earliestCheckIn }),
              ...(dates.latestCheckOut != null && { latestCheckOut: dates.latestCheckOut }),
              ...(dates.excluded != null && { excluded: dates.excluded }),
            },
          },
        }
      })
      return {
        ...state,
        bookDraft: {
          ...state.bookDraft,
          bookingSteps: updatedSteps,
        },
      }
    }

    case EMkBookAction.SET_TIMER_DATA:
      return {
        ...state,
        timerData: action.payload,
      }

    case EMkBookAction.CLEAR_TIMER_DATA:
      return {
        ...state,
        timerData: undefined,
      }

    case EMkBookAction.UPDATE_FIELD: {
      if (!state.bookDraft) return state

      const { stepName, fieldName, fieldValue, fieldType } = action.payload

      let valueToUse = fieldValue

      if (fieldType === 'checkbox' && !valueToUse) {
        valueToUse = ''
      }

      // Ensure only TMkBookStep objects are mapped
      const updatedSteps = state.bookDraft.bookingSteps
        .map((step) => {
          if ('name' in step && Object.values(EMkBookStepName).includes(step.name as EMkBookStepName)) {
            return {
              ...step,
              ...(step.name === stepName && {
                formState: {
                  ...step.formState,
                  [fieldName]: valueToUse as string,
                },
              }),
            }
          }
          return null
        })
        .filter((step): step is TMkBookStep => step !== null)

      const fieldSet = selectFieldSet(updatedSteps)
      const currentSuiteType = state.bookDraft.bookSummary?.stay?.suite?.type
      const stateUpgradeNowField = updatedSteps.find((step) => step.fieldSet?.vipUpgradeNow)?.formState?.vipUpgradeNow
      const stateUpgradeFutureField = updatedSteps.find((step) => step.fieldSet?.vipUpgradeFuture)?.formState?.vipUpgradeFuture
      return {
        ...state,
        bookDraft: {
          ...state.bookDraft,
          bookingSteps: updatedSteps,
          bookSummary: {
            ...state.bookDraft.bookSummary,
            stay: {
              ...state.bookDraft.bookSummary.stay,
              ...(['vipUpgradeNow', 'vipUpgradeFuture'].includes(fieldName) && {
                suiteType: generateSuiteTypeByFieldName(fieldSet, fieldName, valueToUse as string) || String(currentSuiteType),
                emphasizedSuite: true,
              }),
              ...(fieldName === 'vipUpgrade' &&
                (stateUpgradeNowField || stateUpgradeFutureField) && {
                  suiteType: (() => {
                    if (fieldValue === 'now' && stateUpgradeNowField !== '') {
                      return String(generateSuiteTypeByFieldName(fieldSet, 'vipUpgradeNow', (stateUpgradeNowField || currentSuiteType) as string))
                    }
                    if (fieldValue === 'future' && stateUpgradeFutureField !== '') {
                      return String(generateSuiteTypeByFieldName(fieldSet, 'vipUpgradeFuture', (stateUpgradeFutureField || currentSuiteType) as string))
                    }
                    return String(currentSuiteType)
                  })(),
                  emphasizedSuite: false,
                }),
            },
          },
        },
      }
    }

    case EMkBookAction.SET_ERROR:
      return {
        ...state,
        bookingErrors: [...(state.bookingErrors || []), action.payload],
      }

    case EMkBookAction.CLEAR_ERROR:
      return {
        ...state,
        bookingErrors: [],
      }

    case EMkBookAction.SET_LOADING:
      return {
        ...state,
        isLoading: action.payload,
      }

    case EMkBookAction.SET_ORIGINAL_TRIP_SUMMARY:
      return {
        ...state,
        originalTripSummary: action.payload,
      }

    case EMkBookAction.SET_API_CONTEXT:
      return {
        ...state,
        apiContext: action.payload,
      }

    case EMkBookAction.SET_REQUIRES_ADDITIONAL_PAYMENT:
      return {
        ...state,
        requiresAdditionalPayment: action.payload,
      }
    case EMkBookAction.SET_HAS_UPDATED_MODIFY_STEPS:
      return {
        ...state,
        hasUpdatedModifySteps: action.payload,
      }

    default:
      return state
  }
}

export default bookReducer
